import React, { useCallback, useEffect, useState } from "react";
import API_BASE_URL from "../config";
import "./AssignStaffModal.css";

/**
 * "Assign staff" for one event: the studio owner picks which team members can
 * see and upload to this event. Toggling a member on/off calls the
 * event-assignments API (POST/DELETE). An assigned member sees the event in
 * their own events list — see the visibility rules in GET /api/events/:userId.
 *
 * Owner-only: the assignments endpoints return 403 for team members, so this
 * modal is only ever opened from the owner's events view.
 */

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function AssignStaffModal({ event, onClose }) {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);

  const eventId = event?.id;

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/assignments`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not load team members");
      setMembers(Array.isArray(data.members) ? data.members : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = async (member) => {
    if (savingId) return;
    const assign = !member.assigned;
    setSavingId(member.id);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/assignments${assign ? "" : `/${member.id}`}`,
        assign
          ? { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ memberId: member.id }) }
          : { method: "DELETE", headers: authHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Could not update the assignment");
      }
      setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, assigned: assign } : m)));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  if (!event) return null;

  return (
    <div className="asm-overlay" onClick={onClose} role="presentation">
      <div className="asm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="asm-title">
        <h3 id="asm-title">Assign staff</h3>
        <p className="asm-sub">
          Choose which team members can see and upload to <strong>{event.event_name}</strong>.
          Assigned staff see it in their own events list.
        </p>

        {loading ? (
          <p className="asm-loading">Loading…</p>
        ) : members.length === 0 ? (
          <p className="asm-empty">
            You haven’t added any team members yet. Add staff on the Team page, then assign them here.
          </p>
        ) : (
          <ul className="asm-list">
            {members.map((m) => (
              <li key={m.id} className="asm-item">
                <label className="asm-check">
                  <input
                    type="checkbox"
                    checked={!!m.assigned}
                    disabled={savingId === m.id}
                    onChange={() => toggle(m)}
                  />
                  <span className="asm-member">
                    <span className="asm-name">{m.member_title || m.email}</span>
                    <span className="asm-role">
                      {m.member_permission}{m.status && m.status !== "active" ? " · suspended" : ""}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="asm-error" role="alert">{error}</p>}

        <div className="asm-footer">
          <button type="button" className="asm-btn-close" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default AssignStaffModal;
